import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './StaticPages.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// CRA rates (Sep 2024)
const MAX_SINGLES_PENSION = 674.00;   // pw incl supplements
const MAX_COUPLES_PENSION = 1009.00;  // pw combined

const calcCRA = (rentPerPerson, type) => {
  if (type === 'single') {
    return Math.min(73.23, Math.max(0, 0.75 * (rentPerPerson - 74.10)));
  }
  // couples — each person qualifies independently
  return Math.min(103.40, Math.max(0, 0.75 * (rentPerPerson - 123.10)));
};

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;
const pct = (n, d) => `${((n / d) * 100).toFixed(1)}%`;

export default function RentCalculatorPage() {
  const navigate = useNavigate();
  const [totalRent, setTotalRent] = useState(1200);
  const [bedrooms, setBedrooms] = useState(4);
  const [propertyCounts, setPropertyCounts] = useState({});

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    axios.get(`${API}/properties`).then(res => {
      const counts = {};
      res.data.forEach(p => {
        if (p.total_bedrooms) counts[p.total_bedrooms] = (counts[p.total_bedrooms] || 0) + 1;
      });
      setPropertyCounts(counts);
    }).catch(() => {});
  }, []);

  const results = useMemo(() => {
    const rentPerPerson = totalRent / bedrooms;
    const sCRA = calcCRA(rentPerPerson, 'single');
    const cCRA = calcCRA(rentPerPerson, 'couple');
    const sNet = rentPerPerson - sCRA;
    const cNet = rentPerPerson - cCRA;
    return {
      rentPerPerson,
      singles: {
        cra: sCRA,
        net: sNet,
        pensionPct: (sNet / MAX_SINGLES_PENSION) * 100,
        saving: rentPerPerson - sNet,
      },
      couples: {
        cra: cCRA,
        net: cNet,
        pensionPct: (cNet / MAX_COUPLES_PENSION) * 100,
        saving: rentPerPerson - cNet,
      },
      totalWeeklySaving: (sCRA * bedrooms),
    };
  }, [totalRent, bedrooms]);

  // Build comparison table rows for different bedroom counts at current total rent
  const tableRows = useMemo(() => {
    return [3, 4, 5, 6].map(beds => {
      const rpp = totalRent / beds;
      const sCRA = calcCRA(rpp, 'single');
      const cCRA = calcCRA(rpp, 'couple');
      return {
        beds,
        rpp: Math.round(rpp),
        sCRA: Math.round(sCRA),
        sNet: Math.round(rpp - sCRA),
        sPct: ((rpp - sCRA) / MAX_SINGLES_PENSION * 100).toFixed(1),
        cCRA: Math.round(cCRA),
        cNet: Math.round(rpp - cCRA),
        cPct: ((rpp - cCRA) / MAX_COUPLES_PENSION * 100).toFixed(1),
      };
    });
  }, [totalRent]);

  return (
    <div className="static-page" data-testid="rent-calculator-page"><header className="static-header">
        <div className="container">
          <div className="header-content">
            <img src="/logo.png" alt="House Sharing Seniors" className="logo-image" onClick={() => navigate('/')} />
            <nav>
              <button className="btn btn-secondary" onClick={() => navigate('/properties')}>Browse Properties</button>
              <button className="btn btn-primary" onClick={() => navigate('/apply')}>Apply Now</button>
            </nav>
          </div>
        </div>
      </header>

      <section className="page-hero" style={{ background: '#1a2332', padding: '28px 0' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 style={{ color: 'white', marginBottom: 6 }}>HSS Rent Calculator</h1>
          <p style={{ color: 'rgba(255,255,255,0.8)' }}>See your real net rent after Government Rent Assistance (CRA)</p>
        </div>
      </section>

      <section className="page-content">
        <div className="container" style={{ maxWidth: 820 }}>

          {/* Inputs */}
          <div style={{ background: '#f8fafb', borderRadius: 12, padding: '24px 28px', marginBottom: 28, border: '1px solid #e5e7eb' }}>
            <h2 style={{ fontSize: 18, marginBottom: 16, color: '#1a2332' }}>Enter Property Details</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Total Weekly Rent (whole property)
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 18, color: '#1a2332' }}>$</span>
                  <input
                    type="number"
                    value={totalRent}
                    min={300} max={3000} step={50}
                    onChange={e => setTotalRent(Math.max(100, parseInt(e.target.value) || 0))}
                    data-testid="total-rent-input"
                    style={{ flex: 1, padding: '10px 12px', border: '2px solid #d1d5db', borderRadius: 8, fontSize: 18, fontWeight: 700, color: '#1a2332' }}
                  />
                  <span style={{ color: '#6b7280', fontSize: 13 }}>/week</span>
                </div>
                <input type="range" min={300} max={3000} step={50} value={totalRent}
                  onChange={e => setTotalRent(parseInt(e.target.value))}
                  style={{ width: '100%', marginTop: 8 }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#9ca3af' }}>
                  <span>$300</span><span>$3,000</span>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Number of Bedrooms
                </label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[3, 4, 5, 6].map(b => (
                    <button key={b} onClick={() => setBedrooms(b)}
                      data-testid={`bedrooms-${b}`}
                      style={{
                        padding: '10px 20px', borderRadius: 8, border: '2px solid',
                        borderColor: bedrooms === b ? '#1a2332' : '#d1d5db',
                        background: bedrooms === b ? '#1a2332' : 'white',
                        color: bedrooms === b ? 'white' : '#374151',
                        fontWeight: 700, fontSize: 16, cursor: 'pointer'
                      }}>
                      {b} bed
                    </button>
                  ))}
                </div>
                <div style={{ marginTop: 12, padding: '10px 14px', background: '#eff6ff', borderRadius: 8, fontSize: 13, color: '#1d4ed8', fontWeight: 600 }}>
                  Rent per bedroom: <strong>${Math.round(results.rentPerPerson)}/wk</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 28 }}>

            {/* Singles */}
            <div style={{ borderRadius: 12, border: '2px solid #fee2e2', overflow: 'hidden' }}>
              <div style={{ background: '#dc2626', padding: '12px 16px', color: 'white' }}>
                <div style={{ fontWeight: 800, fontSize: 16 }}>SINGLES</div>
                <div style={{ fontSize: 12, opacity: 0.9 }}>Age Pension + CRA</div>
              </div>
              <div style={{ padding: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ color: '#6b7280', fontSize: 14 }}>Gross rent/wk</span>
                  <span style={{ fontWeight: 700 }}>{fmt(results.rentPerPerson)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ color: '#6b7280', fontSize: 14 }}>CRA benefit/wk</span>
                  <span style={{ fontWeight: 700, color: '#059669' }}>− {fmt(results.singles.cra)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ fontWeight: 700, color: '#1a2332' }}>Net rent/wk</span>
                  <span style={{ fontWeight: 900, fontSize: 22, color: '#dc2626' }}>{fmt(results.singles.net)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ color: '#6b7280', fontSize: 13 }}>% of max pension ({fmt(MAX_SINGLES_PENSION)}/wk)</span>
                  <span style={{ fontWeight: 700, color: results.singles.pensionPct < 30 ? '#059669' : results.singles.pensionPct < 50 ? '#d97706' : '#dc2626' }}>
                    {pct(results.singles.net, MAX_SINGLES_PENSION)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                  <span style={{ color: '#6b7280', fontSize: 13 }}>Annual saving vs no CRA</span>
                  <span style={{ fontWeight: 700, color: '#059669' }}>{fmt(results.singles.saving * 52)}/yr</span>
                </div>
              </div>
            </div>

            {/* Couples */}
            <div style={{ borderRadius: 12, border: '2px solid #dbeafe', overflow: 'hidden' }}>
              <div style={{ background: '#1a2332', padding: '12px 16px', color: 'white' }}>
                <div style={{ fontWeight: 800, fontSize: 16 }}>COUPLES</div>
                <div style={{ fontSize: 12, opacity: 0.9 }}>Each qualifies independently</div>
              </div>
              <div style={{ padding: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ color: '#6b7280', fontSize: 14 }}>Gross rent/wk</span>
                  <span style={{ fontWeight: 700 }}>{fmt(results.rentPerPerson)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ color: '#6b7280', fontSize: 14 }}>CRA benefit/wk</span>
                  <span style={{ fontWeight: 700, color: '#059669' }}>− {fmt(results.couples.cra)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ fontWeight: 700, color: '#1a2332' }}>Net rent/wk</span>
                  <span style={{ fontWeight: 900, fontSize: 22, color: '#1a2332' }}>{fmt(results.couples.net)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ color: '#6b7280', fontSize: 13 }}>% of max pension ({fmt(MAX_COUPLES_PENSION)}/wk)</span>
                  <span style={{ fontWeight: 700, color: results.couples.pensionPct < 30 ? '#059669' : results.couples.pensionPct < 50 ? '#d97706' : '#dc2626' }}>
                    {pct(results.couples.net, MAX_COUPLES_PENSION)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                  <span style={{ color: '#6b7280', fontSize: 13 }}>Annual saving vs no CRA</span>
                  <span style={{ fontWeight: 700, color: '#059669' }}>{fmt(results.couples.saving * 52)}/yr</span>
                </div>
              </div>
            </div>
          </div>

          {/* Comparison table */}
          <div className="content-section">
            <h2 style={{ fontSize: 18, marginBottom: 12 }}>Comparison — All Bedroom Counts at ${totalRent.toLocaleString()}/wk Total</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#1a2332', color: 'white' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left' }}>Beds</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>Rent/bed</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>Singles CRA</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', color: '#f87171' }}>Singles Net</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>% Pension</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>Couples CRA</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', color: '#93c5fd' }}>Couples Net</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>% Pension</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map(r => (
                    <tr key={r.beds}
                      style={{ background: r.beds === bedrooms ? '#eff6ff' : r.beds % 2 === 0 ? '#f8fafb' : 'white', fontWeight: r.beds === bedrooms ? 700 : 400 }}>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                        {r.beds} bed {r.beds === bedrooms ? '◀' : ''}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #f3f4f6' }}>${r.rpp}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #f3f4f6', color: '#059669' }}>${r.sCRA}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #f3f4f6', color: '#dc2626', fontWeight: 700 }}>${r.sNet}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #f3f4f6' }}>{r.sPct}%</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #f3f4f6', color: '#059669' }}>${r.cCRA}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #f3f4f6', color: '#1a2332', fontWeight: 700 }}>${r.cNet}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #f3f4f6' }}>{r.cPct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
              CRA rates: Singles max $73.23/wk (threshold $74.10/wk), Couples max $103.40/wk (threshold $123.10/wk). Sep 2024 rates. Seek Centrelink advice for your individual circumstances.
            </p>
          </div>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button className="btn btn-primary btn-large" onClick={() => navigate('/properties')}>
              Browse Available Properties →
            </button>
          </div>

        </div>
      </section>

      <footer className="static-footer">
        <div className="container">
          <p>&copy; 2026 House Sharing Seniors. All rights reserved.</p>
          <p className="footer-links">
            <a href="/privacy">Privacy Policy</a> | <a href="/terms">Terms of Service</a> | <a href="/contact">Contact Us</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
